import { 
  collection, 
  addDoc, 
  getDocs, 
  doc, 
  getDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  orderBy, 
  serverTimestamp,
  Timestamp,
  limit
} from 'firebase/firestore';
import { db, retryOperation } from '../config';

const COLLECTION_NAME = 'products';

export interface Product {
  id?: string;
  name: string;
  price: number;
  category: string;
  description?: string;
  itemType: 'food' | 'retail';
  sku?: string;
  inventory?: number;
  image?: string;
  active: boolean;
  locationIds?: string[]; // Array of location IDs where this product can be sold
  createdAt: Date | Timestamp;
  updatedAt: Date | Timestamp;
}

/**
 * Helper function to normalize product data from Firestore
 */
const normalizeProduct = (doc: any): Product => {
  const data = doc.data();
  return {
    id: doc.id,
    ...data,
    price: typeof data.price === 'number' ? data.price : parseFloat(data.price) || 0,
    createdAt: data.createdAt?.toDate() || new Date(),
    updatedAt: data.updatedAt?.toDate() || new Date()
  } as Product;
};

/**
 * Get all products
 */
export const getAllProducts = async (): Promise<Product[]> => {
  try {
    return await retryOperation(async () => {
      const productsQuery = query(
        collection(db, COLLECTION_NAME),
        orderBy('category'),
        orderBy('name')
      );
      
      const snapshot = await getDocs(productsQuery);
      
      return snapshot.docs.map(normalizeProduct);
    });
  } catch (error) {
    console.error('Error getting products:', error);
    throw error;
  }
};

/**
 * Get products by category
 */
export const getProductsByCategory = async (category: string): Promise<Product[]> => {
  try {
    return await retryOperation(async () => {
      const productsQuery = query(
        collection(db, COLLECTION_NAME),
        where('category', '==', category),
        orderBy('name')
      );
      
      const snapshot = await getDocs(productsQuery);
      
      return snapshot.docs.map(normalizeProduct);
    });
  } catch (error) {
    console.error(`Error getting products for category ${category}:`, error);
    throw error;
  }
};

/**
 * Get products by type (food or retail)
 */
export const getProductsByType = async (itemType: 'food' | 'retail'): Promise<Product[]> => {
  try {
    return await retryOperation(async () => {
      const productsQuery = query(
        collection(db, COLLECTION_NAME),
        where('itemType', '==', itemType),
        orderBy('category'),
        orderBy('name')
      );
      
      const snapshot = await getDocs(productsQuery);
      
      return snapshot.docs.map(normalizeProduct);
    });
  } catch (error) {
    console.error(`Error getting products for type ${itemType}:`, error);
    throw error;
  }
};

/**
 * Get active products only
 */
export const getActiveProducts = async (): Promise<Product[]> => {
  try {
    return await retryOperation(async () => {
      const productsQuery = query(
        collection(db, COLLECTION_NAME),
        where('active', '==', true),
        orderBy('category'),
        orderBy('name')
      );
      
      const snapshot = await getDocs(productsQuery);
      
      return snapshot.docs.map(normalizeProduct);
    });
  } catch (error) {
    console.error('Error getting active products:', error);
    throw error;
  }
};

/**
 * Get a single product by ID
 */
export const getProductById = async (productId: string): Promise<Product | null> => {
  try {
    return await retryOperation(async () => {
      const productDoc = await getDoc(doc(db, COLLECTION_NAME, productId));
      
      if (!productDoc.exists()) {
        return null;
      }
      
      return normalizeProduct(productDoc);
    });
  } catch (error) {
    console.error(`Error getting product with ID ${productId}:`, error);
    throw error;
  }
};

/**
 * Create a new product
 */
export const createProduct = async (productData: Omit<Product, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> => {
  try {
    return await retryOperation(async () => {
      const docRef = await addDoc(collection(db, COLLECTION_NAME), {
        ...productData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      
      return docRef.id;
    });
  } catch (error) {
    console.error('Error creating product:', error);
    throw error;
  }
};

/**
 * Update an existing product
 */
export const updateProduct = async (productId: string, productData: Partial<Product>): Promise<void> => {
  try {
    await retryOperation(async () => {
      const productRef = doc(db, COLLECTION_NAME, productId);
      
      await updateDoc(productRef, {
        ...productData,
        updatedAt: serverTimestamp()
      });
    });
  } catch (error) {
    console.error(`Error updating product with ID ${productId}:`, error);
    throw error;
  }
};

/**
 * Delete a product
 */
export const deleteProduct = async (productId: string): Promise<void> => {
  try {
    await retryOperation(async () => {
      await deleteDoc(doc(db, COLLECTION_NAME, productId));
    });
  } catch (error) {
    console.error(`Error deleting product with ID ${productId}:`, error);
    throw error;
  }
}; 